import React, { useState } from 'react';
import { Box, Button, Chip, FormLabel, IconButton, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, PhotoCamera as PhotoIcon } from '@mui/icons-material';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import FieldSherwood from './FieldSherwood';
import FieldGroupMultioption from './FieldGroupMultioption';
import ICTSelectorOMS from './SmartFields/ICT/ICTSelectorOMS';
import { isMultioptionFieldType, isNotAvailableImageValue, optionValues, parseStoredFieldValue } from '../../utils/fieldValueCoercion';
import { decodeOtherValue, encodeOtherValue } from '../../utils/fieldGroupEdit';

/**
 * One field of a field-group instance, for EDITING a submission on the web
 * (SkincAIr NTD eCRF, SKIN-536). Every control emits the value in the exact shape
 * the Flutter app stores, so an edit never changes how a value is encoded:
 *   chips / skin_tone / select / radio → "value" or '{"value","otherText"}'
 *   text_list   → [{text_list}]
 *   icd11-code  → [{"icd11-code", label}]
 *   multioption / lesion_location / multioption_with_subforms → [{multioption, otherText?}]
 *   image       → untouched (photos are taken and retaken in the app only)
 */
interface FieldGroupFieldInputProps extends LocalizeContextProps {
    field: any;
    value: unknown;
    onChange: (value: unknown) => void;
    name: string;
    country?: string;
    uuidPatient?: string;
    uuidInvestigation?: string;
    uuidSurvey?: string;
}

const EMPTY_META = {
    touched: false, error: undefined, invalid: false, warning: undefined, dirty: false, pristine: true,
    submitFailed: false, submitting: false, valid: true, visited: false, active: false,
};

/**
 * An `exclusive` option (e.g. "None" / "Asymptomatic") cannot coexist with others:
 * picking it clears the rest, picking another clears it — as in the app.
 */
function applyExclusiveOptions(next: any[], previous: any[], options: any[] = []): any[] {
    const exclusive = new Set(options.filter(o => o?.exclusive).map(o => o.value));
    if (exclusive.size === 0) return next;
    const before = new Set(previous.map(e => e?.multioption));
    const added = next.find(e => !before.has(e?.multioption));
    if (!added) return next;
    return exclusive.has(added.multioption)
        ? [added]
        : next.filter(e => !exclusive.has(e?.multioption));
}

const MONK_SCALE = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

function FieldTitle({ field }: { field: any }) {
    return (
        <FormLabel component="legend" required={!!field.required} sx={{ display: 'block', mb: 0.5 }}>
            {field.label}
        </FormLabel>
    );
}

function OtherText(props: { value: string; onChange: (text: string) => void; label: string }) {
    return (
        <TextField size="small" fullWidth variant="outlined" sx={{ mt: 1 }} label={props.label}
            value={props.value} onChange={(e) => props.onChange(e.target.value)} />
    );
}

const FieldGroupFieldInput: React.FC<FieldGroupFieldInputProps> = (props) => {
    const { field, value, onChange, name, translate } = props;
    const t = (id: string, data?: Record<string, unknown>) => translate(id, data as any) as string;
    const otherOptions: string[] | undefined = field.otherOptions;
    const [pickerOpen, setPickerOpen] = useState(false);

    function renderSingleChoiceButtons(options: { value: string; label: string }[]) {
        const decoded = decodeOtherValue(value);
        const showOther = !!otherOptions?.includes(decoded.value);
        return (
            <Box sx={{ mb: 1 }}>
                <FieldTitle field={field} />
                <ToggleButtonGroup exclusive size="small" color="primary" aria-label={field.label}
                    value={decoded.value} sx={{ flexWrap: 'wrap' }}
                    onChange={(_e, next) => {
                        if (next === null || next === undefined) return;
                        onChange(encodeOtherValue(next, otherOptions?.includes(next) ? (decoded.value === next ? decoded.otherText ?? '' : '') : undefined, otherOptions));
                    }}>
                    {options.map(option => (
                        <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none' }}>
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {showOther && (
                    <OtherText label={t('hospital.fg-edit-other-specify')} value={decoded.otherText ?? ''}
                        onChange={(text) => onChange(encodeOtherValue(decoded.value, text, otherOptions))} />
                )}
            </Box>
        );
    }

    function renderTextList() {
        const parsed = parseStoredFieldValue(value, 'text_list');
        const items: string[] = Array.isArray(parsed)
            ? parsed.map((entry: any) => (entry && typeof entry === 'object' ? entry.text_list ?? '' : String(entry)))
            : (typeof parsed === 'string' && parsed !== '' ? [parsed] : []);
        const emit = (next: string[]) => onChange(next.map(text => ({ text_list: text })));
        return (
            <Box sx={{ mb: 1 }}>
                <FieldTitle field={field} />
                {items.map((text, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TextField size="small" fullWidth variant="outlined" value={text}
                            inputProps={{ 'aria-label': `${field.label} ${index + 1}` }}
                            onChange={(e) => emit(items.map((old, i) => (i === index ? e.target.value : old)))} />
                        <IconButton size="small" aria-label={t('hospital.fg-edit-remove-item', { item: text || `${index + 1}` })}
                            onClick={() => emit(items.filter((_old, i) => i !== index))}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => emit([...items, ''])}>
                    {t('hospital.fg-edit-add-item')}
                </Button>
            </Box>
        );
    }

    function renderIcd11() {
        const parsed = parseStoredFieldValue(value, 'multioption');
        const codes: { 'icd11-code': string; label?: string }[] = Array.isArray(parsed) ? parsed as any : [];
        const max = field.maxSelections ? parseInt(field.maxSelections, 10) : undefined;
        const canAdd = !max || codes.length < max;
        return (
            <Box sx={{ mb: 1 }}>
                <FieldTitle field={field} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {codes.map((code, index) => {
                        const text = code.label || code['icd11-code'];
                        return (
                            <Box key={`${code['icd11-code']}-${index}`} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Chip label={`${code['icd11-code']} — ${text}`} />
                                <IconButton size="small" aria-label={t('hospital.fg-edit-remove-item', { item: text })}
                                    onClick={() => onChange(codes.filter((_c, i) => i !== index))}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        );
                    })}
                    {codes.length === 0 && (
                        <Typography variant="body2" color="textSecondary">{t('hospital.fg-edit-icd-none')}</Typography>
                    )}
                </Box>
                {canAdd && !pickerOpen && (
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setPickerOpen(true)}>
                        {t('hospital.fg-edit-icd-add')}
                    </Button>
                )}
                {canAdd && pickerOpen && (
                    <ICTSelectorOMS ino={`fg-${name}`} language={props.activeLanguage?.code || 'en'} type="icd"
                        label={field.label}
                        elementSelected={(selected: any) => {
                            onChange([...codes, { 'icd11-code': selected['icd-code'], label: selected.icd }]);
                            setPickerOpen(false);
                        }} />
                )}
            </Box>
        );
    }

    function renderImage() {
        const parsed = parseStoredFieldValue(value, 'image');
        const count = Array.isArray(parsed) ? parsed.length : (parsed ? 1 : 0);
        // SKIN-646: the operator ticked e.g. "No healthy skin available" in the app.
        const notAvailable = isNotAvailableImageValue(parsed);
        const message = notAvailable
            ? (field.notAvailable?.label || t('hospital.fg-edit-image-not-available'))
            : count > 0 ? t('hospital.fg-edit-image-captured', { count }) : t('hospital.fg-edit-image-missing');
        return (
            <Box sx={{ mb: 1 }}>
                <FieldTitle field={field} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <PhotoIcon fontSize="small" />
                    <Typography variant="body2">
                        {message}
                    </Typography>
                </Box>
            </Box>
        );
    }

    /**
     * `instance-selector` (SKIN-689) — picks among the live instances of another group in the
     * submission (the NTD eCRF's section-D "Applies to lesions" over the visit's C-Lesion
     * instances). Options are injected by the resolver (value = each lesion's groupRecordUuid);
     * the stored value is a plain array of those uuids, the shape the app writes.
     */
    function renderInstanceSelector() {
        const selected: string[] = optionValues(value);
        const options: { value: string; label: string }[] = field.options || [];
        return (
            <Box sx={{ mb: 1 }}>
                <FieldTitle field={field} />
                {options.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        {t('hospital.fg-edit-instance-selector-empty')}
                    </Typography>
                ) : (
                    <ToggleButtonGroup size="small" color="primary" sx={{ flexWrap: 'wrap' }}
                        value={selected} aria-label={field.label}
                        onChange={(_e, next: string[]) => onChange(next)}>
                        {options.map(option => (
                            <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none' }}>
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}
            </Box>
        );
    }

    function renderMultioption() {
        const parsed = parseStoredFieldValue(value, field.type);
        const entries: any[] = Array.isArray(parsed) ? parsed : [];
        return (
            <Box sx={{ mb: 1 }}>
                <FieldGroupMultioption field={{ ...field, name: field.label }} value={entries} mode="input"
                    onChange={(next) => onChange(applyExclusiveOptions(next, entries, field.options))} />
                {entries.map((entry, index) => (otherOptions?.includes(entry?.multioption) ? (
                    <OtherText key={`${entry.multioption}-${index}`} label={t('hospital.fg-edit-other-specify')}
                        value={entry.otherText ?? ''}
                        onChange={(text) => onChange(entries.map((e, i) => (i === index ? { ...e, otherText: text } : e)))} />
                ) : null))}
            </Box>
        );
    }

    function renderWithFieldSherwood() {
        const hasOther = !!otherOptions?.length && (field.type === 'select' || field.type === 'radio');
        const decoded = hasOther ? decodeOtherValue(value) : null;
        const input = {
            name,
            value: decoded ? decoded.value : value,
            // FieldSherwood hands text inputs the DOM event (redux-form unwraps it; we are not redux-form)
            onChange: (next: any) => {
                const raw = next && typeof next === 'object' && 'target' in next ? next.target.value : next;
                if (decoded) {
                    const keep = otherOptions!.includes(raw) ? (raw === decoded.value ? decoded.otherText ?? '' : '') : undefined;
                    onChange(encodeOtherValue(String(raw), keep, otherOptions));
                } else {
                    onChange(raw);
                }
            },
            onBlur: () => {},
            onFocus: () => {},
        };
        return (
            <div style={{ marginBottom: '0.5rem' }}>
                <FieldSherwood input={input} meta={EMPTY_META} type={field.type} label={field.label}
                    options={field.options} required={field.required} country={props.country} color="secondary"
                    fullWidth uuidPatient={props.uuidPatient} uuidInvestigation={props.uuidInvestigation}
                    uuidSurvey={props.uuidSurvey} validation={field.validation} encrypted={field.encrypted} />
                {decoded && otherOptions!.includes(decoded.value) && (
                    <OtherText label={t('hospital.fg-edit-other-specify')} value={decoded.otherText ?? ''}
                        onChange={(text) => onChange(encodeOtherValue(decoded.value, text, otherOptions))} />
                )}
            </div>
        );
    }

    switch (field.type) {
        case 'chips':
            return renderSingleChoiceButtons(field.options || []);
        case 'skin_tone':
            return renderSingleChoiceButtons(MONK_SCALE.map(v => ({ value: v, label: v })));
        case 'text_list':
            return renderTextList();
        case 'icd11-code':
            return renderIcd11();
        case 'image':
            return renderImage();
        case 'instance-selector':
            return renderInstanceSelector();
        default:
            if (isMultioptionFieldType(field.type)) return renderMultioption();
            return renderWithFieldSherwood();
    }
};

export default withLocalize(FieldGroupFieldInput);
